<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Locale } from 'vue-i18n';

const props = defineProps({
  html: { type: Object as PropType<Record<Locale, string>>, required: true },
  variant: { type: String as PropType<'info' | 'warning' | 'error'>, default: 'info' },
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { locale } = useI18n();

const bannerClass = computed(() => `informational-banner-ribbon--${props.variant}`);
const currentHtml = computed(() => props.html[locale.value] || props.html.en || '');
</script>

<template>
  <div
    ref="ribbonEl"
    class="informational-banner-ribbon"
    :class="bannerClass"
  >
    <div class="informational-banner-ribbon__body">
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="informational-banner-ribbon__content"
        v-html="currentHtml"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <button
      class="informational-banner-ribbon__close"
      aria-label="Close"
      @click="emit('close')"
    >
      <ot-icon name="close" />
    </button>
  </div>
</template>

<style lang="scss">
.informational-banner-ribbon {
  --grid-horizontal-ofset: 20%;

  position: relative;
  display: grid;
  padding: 10px 0;
  grid-template-columns: var(--grid-horizontal-ofset) auto var(--grid-horizontal-ofset);

  .md & {
    --grid-horizontal-ofset: 10%;
  }

  .sm & {
    --grid-horizontal-ofset: 5%;
  }

  .xs & {
    --grid-horizontal-ofset: 0;

    padding: 4px;

    .informational-banner-ribbon__content {
      font-size: var(--font-size-sm);
    }
  }

  &__body {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 16px;
    grid-column-start: 2;
    width: 100%;
    min-height: 48px;
    padding-right: 56px; // space for close button
  }

  &__content {
    flex: 1 1 0;
    text-align: center;
    word-break: break-word;
    overflow-wrap: break-word;
    overflow: hidden;
    line-height: 1.4;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-medium);
  }

  &__close {
    position: absolute;
    top: 0;
    right: 0;
    background: none;
    color: var(--v-theme-on-primary);
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    min-height: 48px;
    z-index: 1;
  }

  // Variant styles
  &--info {
    background-color: rgb(var(--v-theme-primary));
    color: var(--v-theme-on-primary);
  }

  &--warning {
    background-color: rgb(var(--v-theme-warning));
    color: var(--text-0);
  }

  &--error {
    background-color: rgb(var(--v-theme-error));
    color: var(--text-0);
  }
}
</style>
